import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { ErrorNotice, Field, Notice, SelectField, Spinner, formatDate } from '../components/ui';
import type { ReminderSchedule, ScheduledReminder } from '../types';

const ANCHOR_LABELS: Record<string, string> = {
  year_opens: 'the year opening',
  due_on: 'the renewal deadline',
  bdi_cutoff: 'the BDI cutoff',
};

const AUDIENCE_LABELS: Record<string, string> = {
  unsettled: 'anyone who has not paid up',
  owing: 'renewed but still owing',
  not_renewed: 'not renewed at all',
  settled: 'paid up',
  '': 'everyone',
};

const STATE_LABELS: Record<string, string> = {
  off: 'switched off',
  upcoming: 'still to come',
  due: 'sending now',
  passed: 'gone by',
};

/**
 * The automatic reminder schedule.
 *
 * Dates are shown rather than offsets, because "30 days after the deadline"
 * is a rule and "30 April" is the thing a committee member can actually
 * check against a calendar. The offsets are still editable underneath.
 */
export function EmailRemindersPage() {
  const [schedule, setSchedule] = useState<ReminderSchedule | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  function load() {
    api
      .get<ReminderSchedule>('/email/reminders')
      .then(setSchedule)
      .catch(setError);
  }

  useEffect(load, []);

  return (
    <>
      <p className="muted small">
        <Link to="/email">← Back to email</Link>
      </p>

      <div className="page-head">
        <h1>Automatic reminders</h1>
      </div>

      {saved ? <Notice kind="success">{saved} saved.</Notice> : null}
      <ErrorNotice error={error} />

      {schedule === null && error === null ? <Spinner label="Loading the schedule" /> : null}

      {schedule !== null && schedule.year === null ? (
        <Notice kind="info">
          No membership year is current, so reminders have no dates to hang off. Set one up under{' '}
          <Link to="/rates">Rates &amp; settings</Link>.
        </Notice>
      ) : null}

      {schedule !== null && schedule.year !== null ? (
        <>
          <div className="card">
            <p className="muted small">
              Dates are for {schedule.year.year}: the year opened {formatDate(schedule.year.opens_on)},
              renewals are due {formatDate(schedule.year.due_on)}, and the BDI cutoff is{' '}
              {formatDate(schedule.year.bdi_cutoff_date)}. Change any of those and every reminder
              moves with it.
            </p>
            <p className="muted small">
              A reminder stays sendable for {schedule.catch_up_days} days after its date, so a night
              the server was down does not lose it — but one that has been sitting unsent for longer
              than that is left alone rather than going out months late.
            </p>
          </div>

          {schedule.reminders.map((reminder) =>
            editing === reminder.id ? (
              <ReminderEditor
                key={reminder.id}
                reminder={reminder}
                onCancel={() => setEditing(null)}
                onSaved={(name) => {
                  setEditing(null);
                  setSaved(name);
                  window.setTimeout(() => setSaved(null), 4000);
                  load();
                }}
              />
            ) : (
              <div className="card" key={reminder.id}>
                <div className="page-head card-head">
                  <h2>{reminder.name}</h2>
                  <div className="head-actions">
                    <span className={`pill pill-reminder-${reminder.state}`}>
                      {STATE_LABELS[reminder.state] ?? reminder.state}
                    </span>
                    <button type="button" onClick={() => setEditing(reminder.id)}>
                      Edit
                    </button>
                  </div>
                </div>
                <dl className="details">
                  <div className="detail">
                    <dt>Sends on</dt>
                    <dd>
                      <strong>{formatDate(reminder.trigger_on)}</strong>
                      <span className="muted block small">
                        {reminder.offset_days === 0
                          ? `on ${ANCHOR_LABELS[reminder.relative_to]}`
                          : `${Math.abs(reminder.offset_days)} days ${reminder.offset_days < 0 ? 'before' : 'after'} ${ANCHOR_LABELS[reminder.relative_to]}`}
                      </span>
                    </dd>
                  </div>
                  <div className="detail">
                    <dt>Goes to</dt>
                    <dd>{AUDIENCE_LABELS[reminder.audience_payment] ?? reminder.audience_payment}</dd>
                  </div>
                  <div className="detail">
                    <dt>Would reach today</dt>
                    <dd>
                      {reminder.would_reach} member{reminder.would_reach === 1 ? '' : 's'}
                    </dd>
                  </div>
                  <div className="detail">
                    <dt>Wording</dt>
                    <dd>
                      <Link to="/email/templates">{reminder.subject ?? reminder.template_code}</Link>
                    </dd>
                  </div>
                </dl>
              </div>
            ),
          )}
        </>
      ) : null}
    </>
  );
}

function ReminderEditor({
  reminder,
  onCancel,
  onSaved,
}: {
  reminder: ScheduledReminder;
  onCancel: () => void;
  onSaved: (name: string) => void;
}) {
  const [relativeTo, setRelativeTo] = useState(reminder.relative_to);
  const [offset, setOffset] = useState(String(reminder.offset_days));
  const [audience, setAudience] = useState(reminder.audience_payment);
  const [active, setActive] = useState(reminder.is_active);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setError(null);
    setBusy(true);
    try {
      await api.put(`/email/reminders/${reminder.id}`, {
        relative_to: relativeTo,
        offset_days: Number(offset),
        audience_payment: audience,
        is_active: active,
      });
      onSaved(reminder.name);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>{reminder.name}</h2>
      <ErrorNotice error={error} />
      <div className="grid">
        <Field
          label="Days before or after"
          type="number"
          value={offset}
          onChange={(e) => setOffset(e.target.value)}
          hint="Negative is before, positive is after."
        />
        <SelectField label="Counted from" value={relativeTo} onChange={(e) => setRelativeTo(e.target.value as ScheduledReminder['relative_to'])}>
          <option value="year_opens">The year opening</option>
          <option value="due_on">The renewal deadline</option>
          <option value="bdi_cutoff">The BDI cutoff</option>
        </SelectField>
        <SelectField label="Goes to" value={audience} onChange={(e) => setAudience(e.target.value)}>
          <option value="unsettled">Anyone who has not paid up</option>
          <option value="owing">Renewed but still owing</option>
          <option value="not_renewed">Not renewed at all</option>
          <option value="settled">Paid up</option>
          <option value="">Everyone</option>
        </SelectField>
      </div>
      <label className="checkbox">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Send this reminder automatically
      </label>
      <div className="actions">
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save reminder'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
